import React, { useEffect, useRef } from 'react';
import { Dropdown, Tooltip } from 'antd';
import { ClockCircleOutlined, DownOutlined, ReloadOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';

/**
 * KAN-111: one refresh control per page — a manual button with the auto-refresh
 * interval on its own dropdown — so everything on the terminal reloads together
 * instead of each card carrying its own button.
 *
 * A single split button rather than a select beside a button: those were two
 * controls of different heights sitting next to each other, and the interval is
 * an option *of* refreshing rather than a separate thing to operate.
 *
 * Auto-refresh defaults to Off on purpose. A tick reloads the terminal record,
 * live telemetry, alerts, usage and the chart, and the Clarus gateway throttles
 * hard enough that we already see 429s from the hourly sync alone. The
 * intervals start at a minute because nothing upstream moves faster: telemetry
 * lands roughly every 45-60s per device, history is a fixed hourly window, and
 * usage updates about hourly.
 */

export const REFRESH_INTERVALS = [
  { label: 'Off', value: 0 },
  { label: '1 min', value: 60_000 },
  { label: '5 min', value: 300_000 },
  { label: '15 min', value: 900_000 },
];

/**
 * A tick is skipped when the tab is hidden, or when the previous refresh has
 * not finished. Left unguarded, a backgrounded tab would keep hammering a
 * rate-limited API for hours with nobody looking at it.
 */
export const shouldTick = (opts: { hidden: boolean; inFlight: boolean }): boolean =>
  !opts.hidden && !opts.inFlight;

/** Label for the button: plain when off, and naming the interval when on. */
export const refreshLabel = (intervalMs: number): string => {
  const match = REFRESH_INTERVALS.find(i => i.value === intervalMs);
  return !intervalMs || !match ? 'Refresh' : `Refresh · ${match.label}`;
};

interface Props {
  onRefresh: () => void;
  loading?: boolean;
  intervalMs: number;
  onIntervalChange: (ms: number) => void;
}

const AutoRefreshControl: React.FC<Props> = ({
  onRefresh,
  loading = false,
  intervalMs,
  onIntervalChange,
}) => {
  // Kept in refs so changing them does not tear down and restart the timer,
  // which would reset the countdown on every parent re-render.
  const onRefreshRef = useRef(onRefresh);
  const loadingRef = useRef(loading);
  onRefreshRef.current = onRefresh;
  loadingRef.current = loading;

  useEffect(() => {
    if (!intervalMs) return;
    const id = setInterval(() => {
      if (shouldTick({ hidden: document.hidden, inFlight: loadingRef.current })) {
        onRefreshRef.current();
      }
    }, intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);

  const menu: MenuProps = {
    selectable: true,
    selectedKeys: [String(intervalMs)],
    items: [
      { key: 'header', label: 'Auto refresh', type: 'group' as const },
      ...REFRESH_INTERVALS.map(i => ({
        key: String(i.value),
        label: i.label,
        icon: i.value ? <ClockCircleOutlined /> : undefined,
      })),
    ],
    onClick: ({ key }) => onIntervalChange(Number(key)),
  };

  return (
    <Tooltip
      title={
        intervalMs
          ? 'Reloads everything on this terminal. Auto refresh is paused while the tab is in the background.'
          : 'Reloads everything on this terminal. Use the arrow to refresh automatically.'
      }
    >
      <Dropdown.Button
        menu={menu}
        loading={loading}
        onClick={onRefresh}
        icon={<DownOutlined />}
        trigger={['click']}
      >
        <ReloadOutlined /> {refreshLabel(intervalMs)}
      </Dropdown.Button>
    </Tooltip>
  );
};

export default AutoRefreshControl;
